import jwt_decode from "jwt-decode";

import { JwtToken } from "../types/user";

export class IgStorage {
  public static TOKEN = "token";
  public static TOKENS = "tokens";
  public static ORG_ID = "orgId";

  private static storeInSession(key: string, value: string | null) {
    if (value) {
      if (typeof value === "object") {
        sessionStorage.setItem(key, JSON.stringify(value));
      } else {
        sessionStorage.setItem(key, value);
      }
    } else {
      sessionStorage.removeItem(key);
    }
  }

  private static storeInLocal(key: string, value: string | object | null) {
    if (value) {
      if (typeof value === "object") {
        localStorage.setItem(key, JSON.stringify(value));
      } else {
        localStorage.setItem(key, value);
      }
    } else {
      localStorage.removeItem(key);
    }
  }

  private static getFromSession(key: string): string | null {
    return sessionStorage.getItem(key);
  }

  private static getFromLocal(key: string): string | null {
    return localStorage.getItem(key);
  }

  private static get sessionAuthToken(): string | null {
    return IgStorage.getFromSession(IgStorage.TOKEN);
  }

  private static set sessionAuthToken(value: string | null) {
    IgStorage.storeInSession(IgStorage.TOKEN, value);
  }

  /**
   * We don't want to expose our token to customers' stores
   */

  private static get localAuthToken(): string | null {
    return IgStorage.getFromLocal(IgStorage.TOKEN);
  }

  private static set localAuthToken(value: string | null) {
    IgStorage.storeInLocal(IgStorage.TOKEN, value);
  }

  private static get localAuthTokenMap(): Record<string, string> {
    const tokenMap = IgStorage.getFromLocal(IgStorage.TOKENS);
    return tokenMap ? JSON.parse(tokenMap) : {};
  }

  private static set localAuthTokenMap(value: Record<string, string | null>) {
    const tokenMap = this.localAuthTokenMap || {};

    IgStorage.storeInLocal(IgStorage.TOKENS, { ...tokenMap, ...value });
  }

  private static updateLocalAuthTokenMap(token: string | null) {
    try {
      if (!token) {
        return;
      }
      const decodedToken = jwt_decode<any>(token);

      if (decodedToken?.orgId) {
        this.localAuthTokenMap = { [decodedToken.orgId]: token };
      }
    } catch (e) {
      console.error(e);
    }
  }

  public static get currentOrgId(): string | null {
    return this.getFromSession(IgStorage.ORG_ID);
  }

  public static set currentOrgId(orgId: string | null) {
    this.storeInSession(IgStorage.ORG_ID, orgId);
  }

  public static get currentToken(): string | null {
    const tokenMap = this.localAuthTokenMap;
    const urlParams = new URLSearchParams(window.location.search);
    const currentOrgId = urlParams.get("widgetAuth") || this.currentOrgId || "";
    if (currentOrgId in tokenMap) {
      return this.localAuthTokenMap[currentOrgId] || null;
    }
    return null;
  }

  public static set currentToken(token: string | null) {
    try {
      if (!token) {
        return;
      }
      const decodedToken = jwt_decode<any>(token);
      this.storeInSession(IgStorage.ORG_ID, decodedToken.orgId);
      this.updateLocalAuthTokenMap(token);
    } catch (e) {
      console.error(e);
    }
  }

  static set authToken(token: string | null) {
    this.sessionAuthToken = token;
    this.localAuthToken = token;
    this.currentToken = token;

    // TODO: Move into widget-only flow
    //this.widgetToken;
  }

  static get authToken(): string | null {
    return this.sessionAuthToken || this.localAuthToken || null;
  }

  static get widgetToken(): string | null {
    const token = this.currentToken;
    if (token) {
      const decodedToken = jwt_decode<any>(token);
      const { exp: expires } = decodedToken;
      if (expires && expires * 1000 > Date.now()) {
        return token;
      }
    }
    return null;
  }

  // helpers

  static clearAuthTokens() {
    this.sessionAuthToken = null;
    this.localAuthToken = null;
  }

  static hasSessionLocalSync(): boolean {
    return !(this.sessionAuthToken && !this.localAuthToken);
  }
}
